import { useState } from 'react'
import {
  Loader2,
  MoreVertical,
  Pencil,
  Plus,
  Trash2,
  UserCog,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  useDeleteTeamGroup,
  useTeamGroups,
  type TeamGroupSummary,
} from '@/api/teamGroups'
import { TeamFormDialog } from './TeamFormDialog'
import { TeamMembersDialog } from './TeamMembersDialog'
import { usePlanRights } from '@/hooks/usePlanRights'
import { PlanBadge } from '@/components/ui/PlanBadge'

export function TeamGroupsSection() {
  const { allowed, planFor } = usePlanRights()
  const groupesPermis = allowed('numeros_partages')
  const { t } = useTranslation()
  const { data: teams = [], isLoading } = useTeamGroups()
  const deleteTeam = useDeleteTeamGroup()

  const [createOpen, setCreateOpen] = useState(false)
  const [editingTeam, setEditingTeam] = useState<TeamGroupSummary | null>(null)
  const [membersTeam, setMembersTeam] = useState<TeamGroupSummary | null>(null)
  const [pendingDelete, setPendingDelete] = useState<TeamGroupSummary | null>(
    null,
  )

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return
    try {
      await deleteTeam.mutateAsync(pendingDelete.id)
      toast.success(
        t('manager.team.groups.deleteSuccess', { name: pendingDelete.name }),
      )
    } catch {
      toast.error(t('manager.team.groups.deleteError'))
    } finally {
      setPendingDelete(null)
    }
  }

  return (
    <section className="pt-8 mt-6 border-t border-slate-200 dark:border-slate-800">
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-slate-900 dark:text-white">
            {t('manager.team.groups.title')}
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            {t('manager.team.groups.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!groupesPermis && <PlanBadge plan={planFor('numeros_partages')} />}
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!groupesPermis}
            onClick={() => setCreateOpen(true)}
          >
            <Plus className="h-4 w-4 mr-1.5" />
            {t('manager.team.groups.newCta')}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
        </div>
      ) : teams.length === 0 ? null : (
        <ul className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 divide-y divide-slate-100 dark:divide-slate-800">
          {teams.map((team) => (
            <TeamRow
              key={team.id}
              team={team}
              onEdit={() => setEditingTeam(team)}
              onManageMembers={() => setMembersTeam(team)}
              onDelete={() => setPendingDelete(team)}
            />
          ))}
        </ul>
      )}

      <TeamFormDialog open={createOpen} onClose={() => setCreateOpen(false)} />
      <TeamFormDialog
        open={!!editingTeam}
        team={editingTeam}
        onClose={() => setEditingTeam(null)}
      />
      {membersTeam && (
        <TeamMembersDialog
          open={!!membersTeam}
          teamId={membersTeam.id}
          teamName={membersTeam.name}
          onClose={() => setMembersTeam(null)}
        />
      )}

      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(open) => !open && setPendingDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('manager.team.groups.deleteTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete &&
                t('manager.team.groups.deleteDescription', {
                  name: pendingDelete.name,
                  count: pendingDelete.member_count,
                })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteTeam.isPending}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                void handleConfirmDelete()
              }}
              disabled={deleteTeam.isPending}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleteTeam.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              {t('manager.team.groups.confirmDelete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  )
}

interface TeamRowProps {
  team: TeamGroupSummary
  onEdit: () => void
  onManageMembers: () => void
  onDelete: () => void
}

/**
 * Une ligne par équipe : la couleur en pastille (elle sert dans le flux et le
 * transfert), le nom, la description, le nombre de membres, et « Membres » en clair.
 */
function TeamRow({ team, onEdit, onManageMembers, onDelete }: TeamRowProps) {
  const { t } = useTranslation()

  return (
    <li className="flex items-center justify-between gap-4 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
      <button type="button" onClick={onManageMembers} className="flex items-center gap-3 min-w-0 text-left flex-1">
        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: team.color_hex }} />
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{team.name}</p>
          {team.description && (
            <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{team.description}</p>
          )}
        </div>
      </button>

      <div className="flex items-center gap-3 flex-shrink-0">
        <span className="text-xs text-slate-500 dark:text-slate-400 hidden sm:inline">
          {t('manager.team.groups.memberCount', { count: team.member_count })}
        </span>
        <Button type="button" variant="outline" size="sm" onClick={onManageMembers}>
          <UserCog className="h-4 w-4 mr-1.5" />
          {t('manager.team.groups.membersCta')}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={onEdit}>
              <Pencil className="h-4 w-4 mr-2" />
              {t('manager.team.groups.edit')}
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={onDelete}
              className="text-red-600 focus:text-red-700 focus:bg-red-50 dark:focus:bg-red-900/30"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {t('manager.team.groups.delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </li>
  )
}
